import dayjs from "dayjs";
import api from "../../../lib/axios";
import { getLocalDayRange } from "../../../lib/date-time";
import type {
  AttendanceListRequest,
  AttendanceListResponse,
  AttendanceStatistics,
} from "../types/attendance.types";

type AttendanceListEnvelope = {
  data?: {
    paging?: {
      count?: number;
      total?: number;
      index?: number;
      page?: number;
      size?: number;
      limit?: number;
    };
    list?: AttendanceListResponse["list"] | null;
  };
  paging?: {
    count?: number;
    total?: number;
    index?: number;
    page?: number;
    size?: number;
    limit?: number;
  };
  list?: AttendanceListResponse["list"] | null;
};

type StatisticsEnvelope = {
  success?: boolean;
  data?: AttendanceStatistics;
  user_message?: string;
  message?: string;
};

export const getAttendanceList = async (
  request: AttendanceListRequest,
  signal?: AbortSignal,
): Promise<AttendanceListResponse> => {
  const response = await api.post<AttendanceListEnvelope>(
    "/v1/portal/attendance-record/list",
    request,
    { signal },
  );
  const payload = response.data.data ?? response.data;

  if (!payload.paging) {
    throw new Error("Invalid attendance list response");
  }

  return {
    paging: {
      count: payload.paging.count ?? payload.paging.total ?? 0,
      index: payload.paging.index ?? payload.paging.page ?? 0,
      size: payload.paging.size ?? payload.paging.limit ?? request.pagination.size,
    },
    list: payload.list ?? [],
  };
};

export const getAttendanceStatistics = async (
  signal?: AbortSignal,
): Promise<AttendanceStatistics> => {
  const response = await api.get<StatisticsEnvelope>("/v1/portal/statistic/attendance", { signal });

  if (response.data.success && response.data.data) return response.data.data;

  throw new Error(
    response.data.user_message ?? response.data.message ?? "Failed to fetch attendance statistics",
  );
};

export const createAttendanceListRequest = (
  filters: import("../types/attendance.types").AttendanceFilters,
  page: number,
): AttendanceListRequest => {
  const filter: Record<string, unknown> = {};

  if (filters.event) filter.keyword = filters.event;
  if (filters.user) filter.user_info = filters.user;
  if (filters.attendee_type && filters.attendee_type !== "all")
    filter.user_type = filters.attendee_type;
  if (filters.event_date) {
    const range = getLocalDayRange(filters.event_date);
    filter.event_date = filters.event_date;
    filter.event_date_filter = { from: range.start, to: range.end };
  }
  if (filters.time_in) filter.time_in = dayjs(filters.time_in, "HH:mm:ss").utc().format("HH:mm:ss");
  if (filters.time_out)
    filter.time_out = dayjs(filters.time_out, "HH:mm:ss").utc().format("HH:mm:ss");
  if (filters.status && filters.status !== "all") filter.status = filters.status;
  if (filters.rewarded_points !== null) filter.point_reward = filters.rewarded_points;

  return {
    pagination: { index: page - 1, size: 20 },
    filter: Object.keys(filter).length ? filter : undefined,
    sorting: { event_date: "DESC" },
  };
};
